import React, { useState } from 'react';
import './Contact.css';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      message: ''
    });
  };

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Get in touch with our team for expert medical billing support</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-section">
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll get back to you within 24 hours.</p>
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="company">Company Name</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="service">Service Interested In</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                  >
                    <option value="">Select a service</option>
                    <option value="medical-billing">Medical Billing Services</option>
                    <option value="prior-auth">Prior Authorization</option>
                    <option value="quality-assurance">Quality Assurance Services</option>
                    <option value="rcm">Revenue Cycle Management</option>
                    <option value="virtual-assistant">Medical Virtual Assistant</option>
                    <option value="patient-intake">Patient Intake and Scheduling</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us about your needs..."
                  ></textarea>
                </div>
                
                <button type="submit" className="btn btn-primary">
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="contact-info-section">
              <h2>Get In Touch</h2>
              <p>Have questions or need expert support with your medical billing? Our team at Nexus Solutions is here to help.</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">📞</div>
                  <div className="contact-text">
                    <h3>Phone</h3>
                    <a href="tel:6570000000">(657) 000-000</a>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">✉️</div>
                  <div className="contact-text">
                    <h3>Email</h3>
                    <a href="mailto:[email]">[email]</a>
                  </div>
                </div>
                
                <div className="contact-item">
                  <div className="contact-icon">📍</div>
                  <div className="contact-text">
                    <h3>Address</h3>
                    <p>Texas, Block B</p>
                  </div>
                </div>
              </div>

              <div className="contact-hours">
                <h3>Business Hours</h3>
                <div className="hours-list">
                  <div className="hours-item">
                    <span>Monday - Friday</span>
                    <span>8:00 AM - 6:00 PM PST</span>
                  </div>
                  <div className="hours-item">
                    <span>Saturday</span>
                    <span>9:00 AM - 2:00 PM PST</span>
                  </div>
                  <div className="hours-item">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>

              <div className="emergency-contact">
                <h3>Emergency Support</h3>
                <p>For urgent matters outside business hours, please call our emergency line:</p>
                <a href="tel:6570000000" className="emergency-phone">(657) 000-000</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How quickly can you start working with us?</h3>
              <p>We can typically begin services within 1-2 weeks of contract signing, depending on your specific needs and system requirements.</p>
            </div>
            <div className="faq-item">
              <h3>Do you work with all types of healthcare providers?</h3>
              <p>Yes, we serve a wide range of healthcare providers including home health, hospice, urgent care, skilled nursing facilities, and more.</p>
            </div>
            <div className="faq-item">
              <h3>What is your pricing structure?</h3>
              <p>Our pricing is customized based on your specific needs, volume, and services required. Contact us for a personalized quote.</p>
            </div>
            <div className="faq-item">
              <h3>How do you ensure HIPAA compliance?</h3>
              <p>We maintain 100% HIPAA compliance through secure systems, encrypted communications, and regular staff training.</p>
            </div>
            <div className="faq-item">
              <h3>Do you integrate with our existing EHR system?</h3>
              <p>Yes, we can integrate with most major EHR/EMR systems to ensure seamless workflow and data transfer.</p>
            </div>
            <div className="faq-item">
              <h3>What kind of reporting do you provide?</h3>
              <p>We provide comprehensive reporting including revenue analytics, denial tracking, and performance metrics on a regular basis.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="contact-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Join the many healthcare providers who trust Nexus Solutions for their revenue cycle management needs.</p>
            <div className="cta-actions">
              <a href="tel:6570000000" className="btn btn-primary">Call Now</a>
              <a href="mailto:[email]" className="btn btn-secondary">Email Us</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
